import React, { useState } from "react";
import { Alert, Button, Form, InputGroup, Spinner } from "react-bootstrap";
import { FaEye, FaEyeSlash, FaLock } from "react-icons/fa";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../../api";

const PASSWORD_PATTERN =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

const ResetPassword = () => {
  const { token } = useParams();
  const [passwords, setPasswords] = useState({ password: "", confirm: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [complete, setComplete] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFeedback(null);

    if (passwords.password !== passwords.confirm) {
      setFeedback({ type: "danger", message: "Passwords do not match." });
      return;
    }

    if (!PASSWORD_PATTERN.test(passwords.password)) {
      setFeedback({
        type: "danger",
        message:
          "Use at least 8 characters with uppercase, lowercase, a number, and a special character.",
      });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/reset-password`, {
        token,
        newPassword: passwords.password,
      });
      setComplete(true);
      setPasswords({ password: "", confirm: "" });
      setFeedback({ type: "success", message: response.data.message });
    } catch (error) {
      setFeedback({
        type: "danger",
        message:
          error.response?.data?.error ||
          "Unable to reset your password. Please request a new link.",
      });
    } finally {
      setLoading(false);
    }
  };

  const passwordField = (name, label, autoComplete) => (
    <Form.Group className="mb-3">
      <Form.Label
        style={{
          fontSize: "0.85rem",
          fontWeight: 500,
          color: "var(--color-text)",
        }}
      >
        {label}
      </Form.Label>
      <InputGroup>
        <InputGroup.Text
          style={{
            backgroundColor: "var(--color-bg)",
            borderColor: "var(--color-border)",
          }}
        >
          <FaLock style={{ color: "var(--color-text-muted)" }} />
        </InputGroup.Text>
        <Form.Control
          type={showPassword ? "text" : "password"}
          value={passwords[name]}
          onChange={(event) =>
            setPasswords((current) => ({
              ...current,
              [name]: event.target.value,
            }))
          }
          autoComplete={autoComplete}
          minLength={8}
          required
        />
        <Button
          type="button"
          variant="outline-secondary"
          aria-label={showPassword ? "Hide passwords" : "Show passwords"}
          onClick={() => setShowPassword((visible) => !visible)}
        >
          {showPassword ? <FaEyeSlash /> : <FaEye />}
        </Button>
      </InputGroup>
    </Form.Group>
  );

  return (
    <div className="auth-page-container">
      <div className="auth-form-column">
        <div className="auth-card" style={{ maxWidth: "440px" }}>
          <div className="text-center mb-4">
            <img
              src="newlogo1.jpg"
              alt="CSD Logo"
              style={{ maxHeight: "58px", width: "auto", objectFit: "contain" }}
            />
          </div>

          <div className="text-start mb-4">
            <h3
              style={{
                color: "var(--color-primary)",
                fontWeight: 600,
                letterSpacing: "-0.5px",
              }}
            >
              Choose a new password
            </h3>
            <p
              style={{
                color: "var(--color-text-secondary)",
                fontSize: "0.9rem",
                margin: 0,
              }}
            >
              Use at least 8 characters with uppercase, lowercase, a number,
              and a special character.
            </p>
          </div>

          {feedback && (
            <Alert variant={feedback.type} style={{ fontSize: "0.85rem" }}>
              {feedback.message}
            </Alert>
          )}

          {!complete ? (
            <Form onSubmit={handleSubmit} className="text-start">
              {passwordField("password", "New Password", "new-password")}
              {passwordField(
                "confirm",
                "Confirm New Password",
                "new-password"
              )}
              <Button
                type="submit"
                disabled={loading}
                className="w-100 mt-2"
                style={{
                  backgroundColor: "var(--color-primary)",
                  borderColor: "var(--color-primary)",
                  borderRadius: "var(--radius-md)",
                  padding: "0.75rem",
                  fontWeight: 500,
                }}
              >
                {loading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Resetting...
                  </>
                ) : (
                  "Reset Password"
                )}
              </Button>
            </Form>
          ) : (
            <Link to="/signin" className="text-decoration-none">
              <Button
                className="w-100"
                style={{
                  backgroundColor: "var(--color-primary)",
                  borderColor: "var(--color-primary)",
                  padding: "0.75rem",
                  fontWeight: 500,
                }}
              >
                Continue to Sign In
              </Button>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
